import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // Define a target for the table body whose rows we'll be sorting
  static targets = ["body"]

  // This method is called by the data-action on the table headers
  sort(event) {
    const header = event.currentTarget
    const columnIndex = header.dataset.columnIndex
    // Determine sort type from data attribute, defaulting to 'text'
    const columnType = header.dataset.columnType || "text" 
    
    // Determine the sort direction
    const currentDirection = header.getAttribute("aria-sort") || "none"
    let nextDirection
    if (currentDirection === "ascending") {
      nextDirection = "descending"
    } else {
      // If descending or unsorted, default to ascending
      nextDirection = "ascending"
    }

    // Sort the table rows
    this.sortTable(columnIndex, columnType, nextDirection)

    // Update the UI to show sort indicators
    this.updateHeaderSortUI(header, nextDirection)
  }

  sortTable(columnIndex, columnType, direction) {
    // Get all rows from the tbody target as an array
    const rows = Array.from(this.bodyTarget.querySelectorAll("tr"))
    const isAscending = direction === "ascending"

    rows.sort((rowA, rowB) => {
      const cellA = rowA.querySelectorAll("td")[columnIndex]?.textContent.trim() || ""
      const cellB = rowB.querySelectorAll("td")[columnIndex]?.textContent.trim() || ""
      // Compare based on the column type (number or text)
      if (columnType === "number") {
      // trim cell to just numbers, remove $ and dots
        let cell_A = cellA.replace(/[$.]/g, "").trim()
        let cell_B = cellB.replace(/[$.]/g, "").trim()
        const numA = parseFloat(cell_A) || 0
        const numB = parseFloat(cell_B) || 0
        return isAscending ? numA - numB : numB - numA
      } 
      else if (columnType === "date") {
        // Parse dates and compare
        const dateA = new Date(cellA)
        const dateB = new Date(cellB)
        return isAscending ? dateA - dateB : dateB - dateA
      }
      else if (columnType === "time:") {
        // Parse time strings and compare
        const timeA = new Date(`1970-01-01T${cellA}`)
        const timeB = new Date(`1970-01-01T${cellB}`)
        return isAscending ? timeA - timeB : timeB - timeA
      }
      else if (columnType === "boolean") {
        // Convert boolean strings to true/false
        const boolA = cellA.toLowerCase() === "true"
        const boolB = cellB.toLowerCase() === "true"
        return isAscending ? (boolA === boolB ? 0 : boolA ? -1 : 1) : (boolA === boolB ? 0 : boolB ? -1 : 1)
      }
      else {
        // Standard string comparison
        return isAscending ? cellA.localeCompare(cellB, undefined, { numeric: true }) : cellB.localeCompare(cellA, undefined, { numeric: true })
      }
    })

    // Re-append the sorted rows to the table body
    this.bodyTarget.innerHTML = "" // Clear existing rows
    rows.forEach(row => this.bodyTarget.appendChild(row))
  }

  updateHeaderSortUI(activeHeader, direction) {
    // Clear previous sort indicators from all headers
    this.element.querySelectorAll("th").forEach(th => {
      th.removeAttribute("aria-sort")
      const indicator = th.querySelector(".sort-indicator")
      if (indicator) {
        indicator.innerHTML = "" // Clear any arrow icon
      }
    })

    // Set the new sort indicator on the active header
    activeHeader.setAttribute("aria-sort", direction)
    let arrow = direction === "ascending" ? " ▲" : " ▼"
    
    // Find or create the indicator span
    let indicatorSpan = activeHeader.querySelector(".sort-indicator")
    if (!indicatorSpan) {
      indicatorSpan = document.createElement("span")
      indicatorSpan.className = "sort-indicator"
      activeHeader.appendChild(indicatorSpan)
    }
    indicatorSpan.innerHTML = arrow
  }
}